/**
 * A scene's time of day as blendable three.js values (ADR 0033): every
 * lighting preset of a scene record (`lighting.presets`, one shape for the
 * town's day and night and the airship's five times) becomes a `Look`, and
 * the view blends from the look shown to the next over `lighting.blend`
 * seconds with a smoothstep, as brad-airship's `airship-time.js` `blend` did
 * (the town's blend is 0: it switches at once, as before). The grade's shadow
 * and highlight tints are luma-normalised per preset and then blended (the
 * prototype's `norm` for its five deck times; lantern-water's day-night blend
 * normalised the same way), except below deck: the airship's rooms take
 * their grade as authored (`airship-focus.js` `applyAirshipGrade` sets the
 * interior's values directly). `lamps` is the night factor everything
 * lamp-lit reads.
 */
import type { Scene } from '@ab/content-schema';
import * as THREE from 'three';
import type { LensUniforms } from '../lens.ts';
import { type SkyLook, skyColorKeys, skyNumberKeys } from '../ship/sky.ts';

type Preset = Scene['lighting']['presets'][string];

export type GradeLook = {
  readonly shadow: THREE.Vector3;
  readonly highlight: THREE.Vector3;
  readonly lift: THREE.Vector3;
  readonly gain: THREE.Vector3;
  readonly vignette: THREE.Vector3;
  readonly haze: THREE.Vector3;
  contrast: number;
  saturation: number;
  hazeAmount: number;
  zone: number;
  glare: number;
};

export type Look = {
  name: string;
  lamps: number;
  exposure: number;
  readonly background: THREE.Color;
  fogDensity: number;
  readonly hemiSky: THREE.Color;
  readonly hemiGround: THREE.Color;
  hemiIntensity: number;
  readonly sunColor: THREE.Color;
  sunIntensity: number;
  /** Toward the light in three's axes (zero when the preset has none: the town's sun stands where the record puts it). */
  readonly sunDirection: THREE.Vector3;
  fill: number;
  glass: number;
  windows: number;
  coolWindows: number;
  bloom: number;
  readonly grade: GradeLook;
  wind: number;
  /** The wind's direction on the ground in three's xz (unit). */
  readonly windDir: THREE.Vector2;
  cloudShadow: number;
  readonly farTint: THREE.Color;
  farMix: number;
  farBlur: number;
  readonly portlight: THREE.Color;
  portlightIntensity: number;
  smoke: number;
  dew: number;
  readonly moteColor: THREE.Color;
  moteAlpha: number;
  moteRadius: number;
  /** The airship's sky (null where the preset has none). */
  readonly sky: SkyLook | null;
};

const lumaNormalized = (c: readonly [number, number, number]): THREE.Vector3 => {
  const l = 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
  return new THREE.Vector3(c[0] / l, c[1] / l, c[2] / l);
};

const skyColor = (c: string | { readonly color: string; readonly gain: number }): THREE.Color => (typeof c === 'string' ? new THREE.Color(c) : new THREE.Color(c.color).multiplyScalar(c.gain));

/** A direction from an azimuth (degrees from north, + toward the bow) and an elevation, in three's axes (`dirFromAz`). */
const fromAzimuth = (azimuth: number, elevation: number): THREE.Vector3 => {
  const a = THREE.MathUtils.degToRad(azimuth);
  const e = THREE.MathUtils.degToRad(elevation);
  return new THREE.Vector3(Math.sin(a) * Math.cos(e), Math.sin(e), -Math.cos(a) * Math.cos(e));
};

const skyOf = (s: NonNullable<Preset['sky']>): SkyLook => {
  const colors = {} as Record<(typeof skyColorKeys)[number], THREE.Color>;
  for (const k of skyColorKeys) colors[k] = skyColor(s.colors[k]);
  const numbers = {} as Record<(typeof skyNumberKeys)[number], number>;
  for (const k of skyNumberKeys) numbers[k] = s[k];
  return { sunDir: fromAzimuth(s.sunAt.azimuth, s.sunAt.elevation), moonDir: fromAzimuth(s.moonAt.azimuth, s.moonAt.elevation), colors, numbers };
};

const asAuthored = (c: readonly [number, number, number]): THREE.Vector3 => new THREE.Vector3(c[0], c[1], c[2]);

/** A preset as a look; `normalise` false keeps the grade's tints as authored (the airship's rooms). */
export const lookOf = (name: string, p: Preset, normalise = true): Look => {
  const tint = normalise ? lumaNormalized : asAuthored;
  const g = p.grade;
  const d = p.sun.direction;
  const w = p.weather;
  return {
    name,
    lamps: p.lamps,
    exposure: p.exposure,
    background: new THREE.Color(p.background),
    fogDensity: p.fogDensityE4 / 1e4,
    hemiSky: new THREE.Color(p.hemisphere.sky),
    hemiGround: new THREE.Color(p.hemisphere.ground),
    hemiIntensity: p.hemisphere.intensity,
    sunColor: new THREE.Color(p.sun.color),
    sunIntensity: p.sun.intensity,
    sunDirection: d === undefined ? new THREE.Vector3() : new THREE.Vector3(d.x, d.h, -d.y).normalize(),
    fill: p.fill ?? 0,
    glass: p.glow?.glass ?? 0,
    windows: p.glow?.windows ?? 0,
    coolWindows: p.glow?.coolWindows ?? 0,
    bloom: p.bloom,
    grade: {
      shadow: tint(g.shadow),
      highlight: tint(g.highlight),
      lift: new THREE.Vector3(...g.lift),
      gain: new THREE.Vector3(...g.gain),
      vignette: new THREE.Vector3(...g.vignette),
      haze: new THREE.Vector3(...g.haze),
      contrast: g.contrast,
      saturation: g.saturation,
      hazeAmount: g.hazeAmount,
      zone: g.zone,
      glare: g.glare,
    },
    wind: w?.wind ?? 0,
    windDir: w === undefined ? new THREE.Vector2(1, 0) : new THREE.Vector2(w.direction.x, -w.direction.y).normalize(),
    cloudShadow: w?.cloudShadow ?? 0,
    farTint: new THREE.Color(p.far?.tint ?? '#000000'),
    farMix: p.far?.mix ?? 0,
    farBlur: p.far?.blur ?? 1,
    portlight: new THREE.Color(p.portlight?.color ?? '#000000'),
    portlightIntensity: p.portlight?.intensity ?? 0,
    smoke: p.smoke ?? 0,
    dew: p.dew ?? 0,
    moteColor: new THREE.Color(p.motes?.color ?? '#000000'),
    moteAlpha: p.motes?.alpha ?? 0,
    moteRadius: p.motes?.radius ?? 0,
    sky: p.sky === undefined ? null : skyOf(p.sky),
  };
};

/** A copy of a look (the blend's `from`). */
export const cloneLook = (l: Look): Look => ({
  ...l,
  background: l.background.clone(),
  hemiSky: l.hemiSky.clone(),
  hemiGround: l.hemiGround.clone(),
  sunColor: l.sunColor.clone(),
  sunDirection: l.sunDirection.clone(),
  grade: { ...l.grade, shadow: l.grade.shadow.clone(), highlight: l.grade.highlight.clone(), lift: l.grade.lift.clone(), gain: l.grade.gain.clone(), vignette: l.grade.vignette.clone(), haze: l.grade.haze.clone() },
  windDir: l.windDir.clone(),
  farTint: l.farTint.clone(),
  portlight: l.portlight.clone(),
  moteColor: l.moteColor.clone(),
  sky: l.sky === null ? null : { sunDir: l.sky.sunDir.clone(), moonDir: l.sky.moonDir.clone(), colors: Object.fromEntries(skyColorKeys.map((k) => [k, l.sky?.colors[k].clone() ?? new THREE.Color()])) as SkyLook['colors'], numbers: { ...l.sky.numbers } },
});

const mix = (a: number, b: number, t: number): number => a + (b - a) * t;

/** Writes the blend of `a` and `b` at `t` (0 a, 1 b) into `out` (the prototype's `blend`: numbers, colours and vectors lerp; a name switches at the half). */
export const blendLook = (out: Look, a: Look, b: Look, t: number): void => {
  out.name = t < 0.5 ? a.name : b.name;
  out.lamps = mix(a.lamps, b.lamps, t);
  out.exposure = mix(a.exposure, b.exposure, t);
  out.background.copy(a.background).lerp(b.background, t);
  out.fogDensity = mix(a.fogDensity, b.fogDensity, t);
  out.hemiSky.copy(a.hemiSky).lerp(b.hemiSky, t);
  out.hemiGround.copy(a.hemiGround).lerp(b.hemiGround, t);
  out.hemiIntensity = mix(a.hemiIntensity, b.hemiIntensity, t);
  out.sunColor.copy(a.sunColor).lerp(b.sunColor, t);
  out.sunIntensity = mix(a.sunIntensity, b.sunIntensity, t);
  out.sunDirection.copy(a.sunDirection).lerp(b.sunDirection, t);
  out.fill = mix(a.fill, b.fill, t);
  out.glass = mix(a.glass, b.glass, t);
  out.windows = mix(a.windows, b.windows, t);
  out.coolWindows = mix(a.coolWindows, b.coolWindows, t);
  out.bloom = mix(a.bloom, b.bloom, t);
  const g = out.grade;
  for (const k of ['shadow', 'highlight', 'lift', 'gain', 'vignette', 'haze'] as const) g[k].copy(a.grade[k]).lerp(b.grade[k], t);
  for (const k of ['contrast', 'saturation', 'hazeAmount', 'zone', 'glare'] as const) g[k] = mix(a.grade[k], b.grade[k], t);
  out.wind = mix(a.wind, b.wind, t);
  out.windDir.copy(a.windDir).lerp(b.windDir, t);
  out.cloudShadow = mix(a.cloudShadow, b.cloudShadow, t);
  out.farTint.copy(a.farTint).lerp(b.farTint, t);
  out.farMix = mix(a.farMix, b.farMix, t);
  out.farBlur = mix(a.farBlur, b.farBlur, t);
  out.portlight.copy(a.portlight).lerp(b.portlight, t);
  out.portlightIntensity = mix(a.portlightIntensity, b.portlightIntensity, t);
  out.smoke = mix(a.smoke, b.smoke, t);
  out.dew = mix(a.dew, b.dew, t);
  out.moteColor.copy(a.moteColor).lerp(b.moteColor, t);
  out.moteAlpha = mix(a.moteAlpha, b.moteAlpha, t);
  out.moteRadius = mix(a.moteRadius, b.moteRadius, t);
  if (out.sky !== null && a.sky !== null && b.sky !== null) {
    out.sky.sunDir.copy(a.sky.sunDir).lerp(b.sky.sunDir, t);
    out.sky.moonDir.copy(a.sky.moonDir).lerp(b.sky.moonDir, t);
    for (const k of skyColorKeys) out.sky.colors[k].copy(a.sky.colors[k]).lerp(b.sky.colors[k], t);
    for (const k of skyNumberKeys) out.sky.numbers[k] = mix(a.sky.numbers[k], b.sky.numbers[k], t);
  }
};

/** A scene's looks by preset name, in the record's order (the pause menu cycles them); a ship room's grade as authored. */
export const looksOf = (scene: Scene): ReadonlyMap<string, Look> => new Map(Object.entries(scene.lighting.presets).map(([name, p]) => [name, lookOf(name, p, scene.kind !== 'ship_room')]));

/** Puts a look's grade into the lens (`light-zones.js`); the glare only where allowed (Medium) and as the caller scales it. */
export const applyLookGrade = (u: LensUniforms, g: GradeLook, glare: number): void => {
  u.gradeShadow.value.copy(g.shadow);
  u.gradeHighlight.value.copy(g.highlight);
  u.gradeLift.value.copy(g.lift);
  u.gradeGain.value.copy(g.gain);
  u.gradeVignette.value.copy(g.vignette);
  u.hazeColor.value.copy(g.haze);
  u.gradeContrast.value = g.contrast;
  u.gradeSaturation.value = g.saturation;
  u.hazeAmount.value = g.hazeAmount;
  u.zoneStrength.value = g.zone;
  u.glare.value = glare;
};

/**
 * The look shown and the blend toward a new one (the view's time of day). `set` starts a blend of `seconds` (0: at
 * once); `step` advances it; `current` is the blended look.
 */
export type LookBlend = { readonly current: Look; set(next: Look, seconds: number): void; step(dt: number): boolean; target(): string };

export const createLookBlend = (start: Look): LookBlend => {
  const current = cloneLook(start);
  let from = cloneLook(start);
  let to = start;
  let t = 1;
  let span = 0;
  return {
    current,
    set(next, seconds) {
      from = cloneLook(current);
      to = next;
      span = seconds;
      t = seconds <= 0 ? 1 : 0;
      blendLook(current, from, to, t);
    },
    step(dt) {
      if (t >= 1) return false;
      t = Math.min(1, t + dt / Math.max(span, 1e-6));
      blendLook(current, from, to, t * t * (3 - 2 * t));
      return true;
    },
    target: () => to.name,
  };
};
